// src/components/TimeCapsuleView.js
import React, { useState } from 'react';
import TimeCapsuleEdit from './TimeCapsuleEdit';
import ConfirmModal from './ConfirmModal';
import { parseLocalDate } from '../utils/storageUtils';
import { apiFetch } from '../utils/apiFetch';
import '../styles/TimeCapsuleView.css';
import { useToast } from './Toast';

const TimeCapsuleView = ({ timeCapsule, isOwner, isOpened, onUpdate, canEdit = true }) => {
    const toast = useToast();
    const [isEditMode, setIsEditMode] = useState(false);
    const [isDeleteLoading, setIsDeleteLoading] = useState(false);
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

    if (!timeCapsule) return null;

    const formatDate = (dateString) => {
        const [year, month, day] = dateString.split('-');
        const date = new Date(year, month - 1, day);
        return date.toLocaleDateString('ko-KR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            weekday: 'long'
        });
    };

    const calculateDDay = (dateString) => {
        const targetDate = parseLocalDate(dateString); // 로컬 자정 기준
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const diffTime = targetDate.getTime() - today.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        if (diffDays > 0) return `D-${diffDays}`;
        if (diffDays === 0) return 'D-Day';
        return `D+${Math.abs(diffDays)}`;
    };

    const handleEditClick = () => {
        // ✅ 수정 가능 여부 확인
        if (!canEdit) {
            toast.error('이미 개봉된 타임캡슐은 수정할 수 없습니다.');
            return;
        }
        
        if (isOpened) {
            toast.error('이미 개봉된 타임캡슐은 수정할 수 없습니다.');
            return;
        }
        
        setIsEditMode(true);
    };

    const handleEditClose = () => {
        setIsEditMode(false);
    };

    const handleEditSuccess = () => {
        setIsEditMode(false);
        if (onUpdate) {
            onUpdate();
        }
    };

    const handleDeleteClick = () => {
        // ✅ 삭제 전 확인
        if (!canEdit || isOpened) {
            toast.error('이미 개봉된 타임캡슐은 삭제할 수 없습니다.');
            return;
        }

        setIsDeleteConfirmOpen(true);
    };

    const handleDeleteConfirm = async () => {
        setIsDeleteLoading(true);

        try {
            const response = await apiFetch(`/timecapsule/${timeCapsule.timecapsuleId}`, {
                method: 'DELETE'
            });

            if (response.ok) {
                toast.success('타임캡슐이 삭제되었습니다.');
                if (onUpdate) {
                    onUpdate();
                }
            } else {
                const errorData = await response.json();
                toast.error(errorData.errorMessage || errorData.message || '타임캡슐 삭제에 실패했습니다.');
            }
        } catch (error) {
            console.error('타임캡슐 삭제 오류:', error);
            toast.error('네트워크 오류가 발생했습니다. 다시 시도해주세요.');
        } finally {
            setIsDeleteLoading(false);
            setIsDeleteConfirmOpen(false);
        }
    };

    // ✅ 수정/삭제 가능 여부 확인
    const canModify = isOwner && !isOpened && canEdit;

    return (
        <>
            <div className="timecapsule-view">
                <div className="timecapsule-info">
                    <div className="timecapsule-date">
                        <span className="open-date">개봉일: {formatDate(timeCapsule.openDate)}</span>
                        <span className={`dday ${isOpened ? 'opened' : 'closed'}`}>
                            {isOpened ? '개봉됨 🎁' : calculateDDay(timeCapsule.openDate)}
                        </span>
                    </div>
                    
                    {canModify && (
                        <div className="timecapsule-actions">
                            <button 
                                className="edit-timecapsule-btn"
                                onClick={handleEditClick}
                                title="수정"
                            >
                                ✏️
                            </button>
                            <button 
                                className="delete-timecapsule-btn"
                                onClick={handleDeleteClick}
                                disabled={isDeleteLoading}
                                title="삭제"
                            >
                                {isDeleteLoading ? '⏳' : '🗑️'}
                            </button>
                        </div>
                    )}
                </div>

                {/* ✅ 수정 불가 안내 메시지 */}
                {isOwner && !canModify && (
                    <div className="modification-notice">
                        {isOpened ? (
                            <small>🔒 개봉된 타임캡슐은 수정할 수 없습니다</small>
                        ) : !canEdit ? (
                            <small>⏰ 타임캡슐이 개봉되어 수정할 수 없습니다</small>
                        ) : null}
                    </div>
                )}

                <div className="timecapsule-content">
                    {isOpened || isOwner ? (
                        <div className="content-visible">
                            {timeCapsule.content.split('\n').map((line, index) => (
                                <p key={index} className="content-line">
                                    {line || '\u00A0'}
                                </p>
                            ))}
                        </div>
                    ) : (
                        <div className="content-hidden">
                            <div className="locked-icon">🔒</div>
                            <p>개봉일까지 기다려주세요!</p>
                            <small>상대방이 비밀 메시지를 남겨두었습니다.</small>
                        </div>
                    )}
                </div>

                {isOwner && (
                    <div className="timecapsule-author">
                        <small>내가 작성한 타임캡슐</small>
                    </div>
                )}
            </div>

            {/* 타임캡슐 수정 모달 */}
            {isEditMode && (
                <TimeCapsuleEdit
                    timeCapsule={timeCapsule}
                    onClose={handleEditClose}
                    onSuccess={handleEditSuccess}
                />
            )}

            {/* 타임캡슐 삭제 확인 모달 */}
            {isDeleteConfirmOpen && (
                <ConfirmModal
                    title="타임캡슐 삭제"
                    message={'정말로 이 타임캡슐을 삭제하시겠습니까?\n삭제된 타임캡슐은 복구할 수 없습니다.'}
                    confirmText="삭제"
                    isLoading={isDeleteLoading}
                    onConfirm={handleDeleteConfirm}
                    onClose={() => setIsDeleteConfirmOpen(false)}
                />
            )}
        </>
    );
};

export default TimeCapsuleView;
